import type { QueryClient } from '@tanstack/react-query';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import Cookies from 'universal-cookie';
import { DEFAULT_FILTERS } from '@/app/(main)/(home)/constants';
import type { User } from '@/models/users';
import services from '@/services';
import { useMainStore } from '@/store';
import { ENV_TYPE } from '@/utils/constants';

const USER_QUERY_STALE_TIME_MS = 10 * 60 * 1000;
const USER_QUERY_GC_TIME_MS = 30 * 60 * 1000;
const AUTH_ERROR_STATUS_CODES = [401, 403];

type UseCurrentUserOptions = {
   enabled?: boolean;
};

function isUnrecoverableAuthError(error: unknown): boolean {
   if (typeof error !== 'object' || error === null || !('status' in error))
      return false;
   const status = (error as { status?: number }).status;
   return AUTH_ERROR_STATUS_CODES.includes(status ?? -1);
}

async function resolveJwtToken(): Promise<string | undefined> {
   const cookies = new Cookies();
   let jwt: string | undefined = cookies.get('jwt');

   if (process.env.ENV_TYPE === ENV_TYPE.LOCAL && !jwt) {
      jwt = await services.auth.getLoginToken();
      cookies.set('jwt', jwt, { path: '/' });
   }

   return jwt;
}

function prefetchSurveysForNewUser(queryClient: QueryClient, user: User): void {
   const clientId = user.site?.client_id;
   if (!clientId) return;

   queryClient.prefetchInfiniteQuery({
      queryKey: ['surveys-infinite', [DEFAULT_FILTERS.EXCLUDE_CLOSED], '', []],
      queryFn: async ({ pageParam = 1 }) =>
         services.survey.getSurveys({
            page: pageParam as number,
            sorts: [],
            filters: [DEFAULT_FILTERS.EXCLUDE_CLOSED],
            value: '',
            userId: user.id,
         }),
      initialPageParam: 1,
   });

   queryClient.prefetchQuery({
      queryKey: [
         'active-count',
         clientId,
         user.id,
         [DEFAULT_FILTERS.EXCLUDE_CLOSED],
         '',
      ],
      queryFn: async () => {
         const result = await services.survey.getSurveys({
            page: 1,
            filters: [DEFAULT_FILTERS.EXCLUDE_CLOSED],
            value: '',
            userId: user.id,
            optimizedForCount: true,
         });
         return result.meta?.total ?? 0;
      },
   });
}

export const useCurrentUser = ({ enabled = true }: UseCurrentUserOptions) => {
   const { currentUser, setCurrentUser } = useMainStore();
   const queryClient = useQueryClient();

   const { data, isLoading, error } = useQuery({
      queryKey: ['current-user'],
      enabled,
      queryFn: async () => {
         const jwt = await resolveJwtToken();

         if (!jwt) {
            window.location.href = `${process.env.PORTAIL_URL}/auth`;
            throw new Error('No authentication token');
         }

         return services.user.getCurrentUser();
      },
      staleTime: USER_QUERY_STALE_TIME_MS,
      gcTime: USER_QUERY_GC_TIME_MS,
      retry: (failureCount, error) =>
         !isUnrecoverableAuthError(error) && failureCount < 1,
   });

   useEffect(() => {
      if (!enabled || !data) return;
      setCurrentUser(data);
      prefetchSurveysForNewUser(queryClient, data);
   }, [data, enabled, setCurrentUser, queryClient]);

   return {
      currentUser: data ?? currentUser,
      isLoading,
      error,
   };
};
